import type {
    IUpdateUserProfile,
    IUserResponse,
    TClinicalSpecialty,
    TProfessionalRole,
} from '@escloud/types';
import { useMemo, type ReactNode } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getCountryOptions } from '@/lib/countries';

/**
 * The professional profile as a form draft (ESCLOUD-333). One component renders
 * it on the welcome page (first completion) and in Settings (later edits), so
 * the two cannot drift on which fields exist. Licence number and authority are
 * not collected here: self-declared, never verified, and dropped from
 * onboarding; the columns stay.
 */
export interface IProfessionalProfileDraft {
    givenName: string;
    familyName: string;
    professionalRole: TProfessionalRole | '';
    specialty: TClinicalSpecialty | '';
    countryOfPractice: string;
    institution: string;
}

const ROLE_LABELS: Record<TProfessionalRole, string> = {
    physician: 'Physician',
    resident_fellow: 'Resident / Fellow',
    nurse_technologist: 'Nurse / Technologist',
    medical_device_professional: 'Medical device professional',
    researcher: 'Researcher',
    other: 'Other',
};

const SPECIALTY_LABELS: Record<TClinicalSpecialty, string> = {
    vascular_surgery: 'Vascular surgery',
    interventional_radiology: 'Interventional radiology',
    interventional_oncology: 'Interventional oncology',
    cardiology: 'Cardiology',
    other: 'Other',
    not_applicable: 'Not applicable (non-clinical role)',
};

interface ISelectOption {
    value: string;
    label: string;
}

/** Total records over the unions: a new enum member is a compile error here, not a missing option. */
function toOptions(labels: Record<string, string>): ISelectOption[] {
    return Object.entries(labels).map(([value, label]) => ({ value, label }));
}

const ROLE_OPTIONS: readonly ISelectOption[] = toOptions(ROLE_LABELS);
const SPECIALTY_OPTIONS: readonly ISelectOption[] = toOptions(SPECIALTY_LABELS);

export function draftFromUser(
    user: IUserResponse | undefined,
    institutionFallback = ''
): IProfessionalProfileDraft {
    return {
        givenName: user?.givenName ?? '',
        familyName: user?.familyName ?? '',
        professionalRole: user?.professionalRole ?? '',
        specialty: user?.specialty ?? '',
        countryOfPractice: user?.countryOfPractice ?? '',
        institution: user?.institution ?? institutionFallback,
    };
}

export function isDraftComplete(draft: IProfessionalProfileDraft): boolean {
    return Object.values(draft).every((value) => value.trim().length > 0);
}

export function draftToPatch(draft: IProfessionalProfileDraft): IUpdateUserProfile {
    return {
        givenName: draft.givenName.trim(),
        familyName: draft.familyName.trim(),
        professionalRole: draft.professionalRole as TProfessionalRole,
        specialty: draft.specialty as TClinicalSpecialty,
        countryOfPractice: draft.countryOfPractice,
        institution: draft.institution.trim(),
    };
}

export function ProfessionalProfileFields({
    value,
    onChange,
    idPrefix = 'profile',
    disabled,
}: {
    value: IProfessionalProfileDraft;
    onChange: (next: IProfessionalProfileDraft) => void;
    idPrefix?: string;
    disabled?: boolean;
}) {
    const countryOptions = useMemo(
        () => getCountryOptions().map(({ code, name }) => ({ value: code, label: name })),
        []
    );
    const set = <K extends keyof IProfessionalProfileDraft>(key: K, next: IProfessionalProfileDraft[K]) =>
        onChange({ ...value, [key]: next });

    return (
        <div className="grid gap-3.5 sm:grid-cols-2">
            <TextField
                id={`${idPrefix}-given-name`}
                label="First name"
                value={value.givenName}
                onChange={(v) => set('givenName', v)}
                autoComplete="given-name"
                disabled={disabled}
            />
            <TextField
                id={`${idPrefix}-family-name`}
                label="Last name"
                value={value.familyName}
                onChange={(v) => set('familyName', v)}
                autoComplete="family-name"
                disabled={disabled}
            />
            <SelectField
                id={`${idPrefix}-role`}
                label="Role"
                placeholder="Select a role"
                value={value.professionalRole}
                onChange={(v) => set('professionalRole', v as TProfessionalRole)}
                options={ROLE_OPTIONS}
                disabled={disabled}
            />
            <SelectField
                id={`${idPrefix}-specialty`}
                label="Specialty"
                placeholder="Select a specialty"
                value={value.specialty}
                onChange={(v) => set('specialty', v as TClinicalSpecialty)}
                options={SPECIALTY_OPTIONS}
                disabled={disabled}
            />
            <SelectField
                id={`${idPrefix}-country`}
                label="Country of practice"
                placeholder="Select a country"
                value={value.countryOfPractice}
                onChange={(v) => set('countryOfPractice', v)}
                options={countryOptions}
                disabled={disabled}
            />
            <TextField
                id={`${idPrefix}-institution`}
                label="Institution"
                value={value.institution}
                onChange={(v) => set('institution', v)}
                autoComplete="organization"
                disabled={disabled}
            />
        </div>
    );
}

function Field({ id, label, children }: { id: string; label: string; children: ReactNode }) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={id}>{label}</Label>
            {children}
        </div>
    );
}

function TextField({
    id,
    label,
    value,
    onChange,
    autoComplete,
    disabled,
}: {
    id: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    autoComplete?: string;
    disabled?: boolean;
}) {
    return (
        <Field id={id} label={label}>
            <Input
                id={id}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                autoComplete={autoComplete}
                disabled={disabled}
            />
        </Field>
    );
}

function SelectField({
    id,
    label,
    placeholder,
    value,
    onChange,
    options,
    disabled,
}: {
    id: string;
    label: string;
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
    options: readonly ISelectOption[];
    disabled?: boolean;
}) {
    return (
        <Field id={id} label={label}>
            <Select value={value} onValueChange={onChange} disabled={disabled}>
                <SelectTrigger id={id}>
                    <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent>
                    {options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </Field>
    );
}
